import { forwardRef, type InputHTMLAttributes, useId, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Input } from "./Input";
import { Label } from "./Label";

/**
 * FormField — accessible form field wrapper.
 *
 * Connects label, input, and error message with proper ARIA attributes via
 * useId(), so screen readers announce the error/hint alongside the field.
 */
interface FormFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  wrapperClassName?: string;
  id?: string;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    { label, error, hint, required, wrapperClassName, id: customId, className, ...props },
    ref,
  ) => {
    const generatedId = useId();
    const id = customId || generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    const describedBy = [error ? errorId : null, hint && !error ? hintId : null]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={cn("space-y-2", wrapperClassName)}>
        <Label htmlFor={id} required={required}>
          {label}
        </Label>

        <Input
          ref={ref}
          id={id}
          error={!!error}
          aria-invalid={!!error}
          aria-describedby={describedBy || undefined}
          aria-required={required}
          className={className}
          {...props}
        />

        {error && (
          <p id={errorId} className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={hintId} className="text-sm text-text-muted">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

FormField.displayName = "FormField";

interface FormTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  wrapperClassName?: string;
}

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  (
    { label, error, hint, required, wrapperClassName, id: customId, className, ...props },
    ref,
  ) => {
    const generatedId = useId();
    const id = customId || generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    const describedBy = [error ? errorId : null, hint && !error ? hintId : null]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={cn("space-y-2", wrapperClassName)}>
        <Label htmlFor={id} required={required}>
          {label}
        </Label>

        <textarea
          ref={ref}
          id={id}
          aria-invalid={!!error}
          aria-describedby={describedBy || undefined}
          aria-required={required}
          className={cn(
            "flex min-h-[80px] w-full rounded-md border bg-bg-card px-3 py-2 text-sm",
            "placeholder:text-text-muted",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error ? "border-danger" : "border-border",
            className,
          )}
          {...props}
        />

        {error && (
          <p id={errorId} className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={hintId} className="text-sm text-text-muted">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

FormTextarea.displayName = "FormTextarea";

interface FormSelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  wrapperClassName?: string;
  children: ReactNode;
}

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    { label, error, hint, required, wrapperClassName, id: customId, className, children, ...props },
    ref,
  ) => {
    const generatedId = useId();
    const id = customId || generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    const describedBy = [error ? errorId : null, hint && !error ? hintId : null]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={cn("space-y-2", wrapperClassName)}>
        <Label htmlFor={id} required={required}>
          {label}
        </Label>

        <select
          ref={ref}
          id={id}
          aria-invalid={!!error}
          aria-describedby={describedBy || undefined}
          aria-required={required}
          className={cn(
            "flex h-10 w-full rounded-md border bg-bg-card px-3 py-2 text-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error ? "border-danger" : "border-border",
            className,
          )}
          {...props}
        >
          {children}
        </select>

        {error && (
          <p id={errorId} className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={hintId} className="text-sm text-text-muted">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

FormSelect.displayName = "FormSelect";
